import { OnDestroy, OnInit, } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { Auth } from '@freelancer/auth';
import { Datastore, OrderByDirection, uniqWith, } from '@freelancer/datastore';
import { Feature } from '@freelancer/feature-flags';
import { ModalRef } from '@freelancer/ui';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { ButtonColor } from '@freelancer/ui/button';
import { HeadingType, HeadingWeight } from '@freelancer/ui/heading';
import { Margin } from '@freelancer/ui/margin';
import { FontColor, TextSize } from '@freelancer/ui/text';
import { isDefined, toNumber } from '@freelancer/utils';
import { EnterpriseApi, RoleApi } from 'api-typings/common/common';
import * as Rx from 'rxjs';
import { debounceTime, filter, map, startWith, take } from 'rxjs/operators';
var SkillsEditModalComponent = /** @class */ (function () {
    function SkillsEditModalComponent(auth, datastore, fb, modalRef) {
        this.auth = auth;
        this.datastore = datastore;
        this.fb = fb;
        this.modalRef = modalRef;
        this.BannerAlertType = BannerAlertType;
        this.ButtonColor = ButtonColor;
        this.Feature = Feature;
        this.FontColor = FontColor;
        this.TextSize = TextSize;
        this.HeadingType = HeadingType;
        this.HeadingWeight = HeadingWeight;
        this.Margin = Margin;
        this.OTHERS_CATEGORY = { name: 'Others', id: 99 };
        this.DELOITTE_SKILLS_FILTER = [88, 252]; // Dating and XXX skills
        this._busySavingSkills$ = new Rx.Subject();
        this._changesMade$ = new Rx.BehaviorSubject(false);
        this._errorSaving$ = new Rx.Subject();
        this._selectedCategory$ = new Rx.Subject();
        this._selectedSkills$ = new Rx.BehaviorSubject([]);
        this._loadingSearchedForSkills$ = new Rx.Subject();
        this._showSkillsLimitReachedDialogSubject$ = new Rx.BehaviorSubject(false);
        this._selectedSkillSubject$ = new Rx.Subject();
        this._undefinedMembershipPackageSubject$ = new Rx.Subject();
        this._maxSkillsSubject$ = new Rx.Subject();
        this.busySavingSkills$ = this._busySavingSkills$.asObservable();
        this.errorSaving$ = this._errorSaving$.asObservable();
        this.selectedCategory$ = this._selectedCategory$.asObservable();
        this.selectedSkills$ = this._selectedSkills$.asObservable();
        this.loadingSearchedForSkills$ = this._loadingSearchedForSkills$.asObservable();
        this.showSkillsLimitReachedDialog$ = this._showSkillsLimitReachedDialogSubject$.asObservable();
        this.skillFormGroup = new FormGroup({});
        this.skillSearchControl = new FormControl('');
    }
    SkillsEditModalComponent.prototype.ngOnInit = function () {
        var _this = this;
        var allSkillsCollection$ = this.datastore.collection('skills', function (q) { return q.orderBy('activeProjectCount', OrderByDirection.DESC); });
        this.userSkillsDocument = this.datastore.document('userSkills');
        var membershipBenefitsDocument$ = this.datastore.document('membershipBenefits', 'skills_limit');
        this.userId$ = this.auth.getUserId().pipe(map(function (id) { return toNumber(id); }));
        this.userDocument = this.datastore.document('users', this.userId$);
        // filtering out xxx and dating skills for deloitte
        // TODO: filter these on backend (T131917)
        var allSkills$ = Rx.combineLatest([
            allSkillsCollection$.valueChanges(),
            this.userDocument.valueChanges(),
        ]).pipe(map(function (_a) {
            var allSkills = _a[0], user = _a[1];
            return user.enterpriseIds &&
                user.enterpriseIds.includes(EnterpriseApi.DELOITTE_DC)
                ? allSkills.filter(function (job) { return !_this.DELOITTE_SKILLS_FILTER.includes(job.id); })
                : allSkills;
        }));
        var userDocument = this.datastore.document('users', this.userId$);
        this.isFreelancer$ = userDocument
            .valueChanges()
            .pipe(map(function (user) { return user.role === RoleApi.FREELANCER; }));
        // Set a form group of all skills and pre-fill the selected skills.
        Rx.combineLatest([this.userSkillsDocument.valueChanges(), allSkills$])
            .pipe(take(1))
            .toPromise()
            .then(function (_a) {
            var usersSkills = _a[0], skills = _a[1];
            var selectedSkills = [];
            skills.forEach(function (skill) {
                var hasSkill = usersSkills.skills.includes(skill.id);
                if (hasSkill) {
                    selectedSkills.push(skill);
                }
                _this.skillFormGroup.addControl(skill.seoUrl, _this.fb.control(hasSkill));
            });
            _this._selectedSkills$.next(selectedSkills);
        });
        this.maxSkillsLimit$ = Rx.combineLatest([
            membershipBenefitsDocument$.valueChanges(),
            this._maxSkillsSubject$.pipe(startWith(undefined)),
        ]).pipe(map(function (_a) {
            var benefit = _a[0], maxSkills = _a[1];
            return maxSkills || benefit.value;
        }));
        this.overMaxSkillsLimit$ = Rx.combineLatest([
            this.maxSkillsLimit$,
            this._selectedSkills$,
        ]).pipe(map(function (_a) {
            var maxSkills = _a[0], selectedSkills = _a[1];
            return selectedSkills.length > maxSkills;
        }));
        this.showSkillsLimitReachedDialogSubscription = Rx.combineLatest([
            this.isFreelancer$,
            this.overMaxSkillsLimit$,
            this._selectedSkillSubject$.pipe(filter(isDefined)),
        ]).subscribe(function (_a) {
            var isFreelancer = _a[0], overMaxSkillsLimit = _a[1];
            if (isFreelancer && overMaxSkillsLimit) {
                _this._showSkillsLimitReachedDialogSubject$.next(true);
            }
            else if (!isFreelancer && overMaxSkillsLimit) {
                _this.handleUndefinedMembershipPackage();
            }
        });
        this.skillSearchControl.valueChanges.subscribe(function () {
            _this._loadingSearchedForSkills$.next(true);
        });
        this.showSaveButton$ = Rx.combineLatest([
            this.overMaxSkillsLimit$,
            this._changesMade$,
        ]).pipe(map(function (_a) {
            var overMaxSkillCount = _a[0], changesMade = _a[1];
            return !overMaxSkillCount && changesMade;
        }));
        this.matchingJobCount$ = this.selectedSkills$.pipe(map(function (skills) {
            return skills.reduce(function (prev, cur) { return prev + cur.activeProjectCount; }, 0);
        }));
        this.filteredSkills$ = Rx.combineLatest([
            allSkills$,
            this.selectedCategory$.pipe(startWith(undefined)),
            this.skillSearchControl.valueChanges.pipe(startWith(undefined), debounceTime(500)),
        ]).pipe(map(function (_a) {
            var skills = _a[0], selectedCategory = _a[1], searchInputText = _a[2];
            // If the user has used the skill search input, prioritise over the
            // selected category. Search on both the skill name and category name.
            if (searchInputText) {
                var searchTerm_1 = searchInputText.toLowerCase();
                var filteredSkills = skills.filter(function (skill) {
                    return (skill.category &&
                        skill.category.name &&
                        skill.category.name.toLowerCase().includes(searchTerm_1)) ||
                        skill.name.toLowerCase().includes(searchTerm_1);
                });
                _this._loadingSearchedForSkills$.next(false);
                return filteredSkills;
            }
            // If a category has been selected return all skills with that category.
            if (selectedCategory) {
                var filteredSkills = skills.filter(function (skill) {
                    return skill.category && skill.category.id === selectedCategory.id;
                });
                _this._loadingSearchedForSkills$.next(false);
                return filteredSkills;
            }
            _this._loadingSearchedForSkills$.next(false);
            // The user has not selected a category or used the search box.
            return [];
        }));
        this.categories$ = allSkills$.pipe(
        // Take only unique categories from all active skills.
        map(function (skills) { return uniqWith(skills.map(function (skill) { return skill.category; }).filter(isDefined), function (t1, t2) { return t1.id === t2.id; })
            // Remove the 'Others' category and order by id.
            .filter(function (category) { return category.id !== _this.OTHERS_CATEGORY.id; })
            .sort(function (a, b) { return a.id - b.id; }).concat([
            // Appends the 'Others' category to the end of the list.
            _this.OTHERS_CATEGORY,
        ]); }));
        this.selectedSkillThatExceededTheLimitSubscription = Rx.combineLatest([
            this._selectedSkillSubject$,
            this.overMaxSkillsLimit$,
            this.showSkillsLimitReachedDialog$,
        ]).subscribe(function (_a) {
            var selectedSkill = _a[0], overMaxSkillsLimit = _a[1], showLimitReachedModal = _a[2];
            if (overMaxSkillsLimit && showLimitReachedModal) {
                _this.handleDeselectedSkillSelection(selectedSkill);
            }
        });
        this.showExceededLimitErrorMessage$ = Rx.combineLatest([
            this._showSkillsLimitReachedDialogSubject$,
            this._undefinedMembershipPackageSubject$,
        ]).pipe(map(function (_a) {
            var showSkillsLimitReachedDialog = _a[0];
            return !showSkillsLimitReachedDialog;
        }));
    };
    SkillsEditModalComponent.prototype.handleUndefinedMembershipPackage = function () {
        this._undefinedMembershipPackageSubject$.next();
    };
    SkillsEditModalComponent.prototype.handleSelectedCategory = function (selectedCategory) {
        this._selectedCategory$.next(selectedCategory);
        // Clear out the input text when selecting a category.
        this.skillSearchControl.reset(undefined);
    };
    SkillsEditModalComponent.prototype.handleSelectedSkill = function (selectedSkill) {
        var _this = this;
        this._selectedSkillSubject$.next(selectedSkill);
        this._changesMade$.next(true);
        this.selectedSkills$
            .pipe(take(1))
            .toPromise()
            .then(function (skills) {
            _this._selectedSkills$.next(skills.concat([selectedSkill]));
        });
    };
    SkillsEditModalComponent.prototype.handleDeselectedSkill = function (deselectedSkill) {
        var _this = this;
        this._changesMade$.next(true);
        this.selectedSkills$
            .pipe(take(1))
            .toPromise()
            .then(function (skills) {
            _this._selectedSkills$.next(skills.filter(function (skill) { return skill !== deselectedSkill; }));
        });
    };
    SkillsEditModalComponent.prototype.handleSaveSkills = function () {
        var _this = this;
        this._busySavingSkills$.next(true);
        this._errorSaving$.next(false);
        Rx.combineLatest([this.userId$, this.selectedSkills$])
            .pipe(take(1))
            .toPromise()
            .then(function (_a) {
            var userId = _a[0], selectedSkills = _a[1];
            var skillIds = selectedSkills.map(function (skill) { return skill.id; });
            var userSkill = {
                id: userId,
                skills: skillIds,
            };
            // Only update the skills if the user has at least 1 skill selected.
            if (skillIds.length > 0) {
                _this.userSkillsDocument
                    .update(userSkill)
                    .then(function (response) { return _this.handleSaveResponse(response); });
            }
            else {
                _this.userSkillsDocument
                    .remove()
                    .then(function (response) { return _this.handleSaveResponse(response); });
            }
        });
    };
    SkillsEditModalComponent.prototype.handleSaveResponse = function (response) {
        this._busySavingSkills$.next(false);
        if (response.status === 'error') {
            // TODO: implement a toast error message when live.
            this._errorSaving$.next(true);
            return;
        }
        // Close the modal with a boolean of if any changes were made. This is used
        // to determine if the user profile top skills needs to be updated.
        this.modalRef.close(this._changesMade$.getValue());
    };
    SkillsEditModalComponent.prototype.handleDeselectedSkillSelection = function (deselectedSkill) {
        var skill = this.skillFormGroup.get(deselectedSkill.seoUrl);
        if (skill) {
            skill.setValue(null);
        }
        this.handleDeselectedSkill(deselectedSkill);
    };
    /**
     * Handles the membership upsell close with firing
     * the _maxSkillsSubject$ if the newMaxSkillsLimit was given
     *
     * @param newMaxSkillsLimit This is the new max skills limit
     *                          after updating the membership
     */
    SkillsEditModalComponent.prototype.handleLimitReachedDialogCloseClicked = function (newMaxSkillsLimit) {
        this._showSkillsLimitReachedDialogSubject$.next(false);
        if (newMaxSkillsLimit) {
            this._maxSkillsSubject$.next(newMaxSkillsLimit);
        }
    };
    SkillsEditModalComponent.prototype.ngOnDestroy = function () {
        if (this.showSkillsLimitReachedDialogSubscription) {
            this.showSkillsLimitReachedDialogSubscription.unsubscribe();
        }
        if (this.selectedSkillThatExceededTheLimitSubscription) {
            this.selectedSkillThatExceededTheLimitSubscription.unsubscribe();
        }
    };
    return SkillsEditModalComponent;
}());
export { SkillsEditModalComponent };
